import { EVA_RESEARCH_NAME } from "constants/app.constants";
import {
  FORECAST_KEY,
  FORECAST_TRUMPING_FOLDER_ID,
  FORECAST_TRUMPING_FOLDER_NAME,
  MCDANIEL_SEARCH_FOLDER_NAME,
  NOVI_FORECAST_SOURCE,
  NOVI_SEARCH_FOLDER_NAME,
  WORKING_FORECAST_FOLDER_NAME,
  WORKING_FORECAST_SOURCE,
  isForecastTrumpingSource
} from "constants/settings.constants";

/**
 * The suffix that qualifies a forecast-bearing property for a given forecast source.
 *
 * Three forms, and the differences matter to the backend:
 *  - Forecast Trumping: rescat only, no source — the service picks the source per well
 *  - EVA Research:      nothing at all
 *  - anything else:     source then rescat, e.g. `.shaleProfileDB.P+PDP`
 */
export function getForecastPropertySuffix(
  selectedForecastFolder: Maybe<string>,
  selectedRescat: Maybe<string>
): string {
  if (isForecastTrumpingSource(selectedForecastFolder)) {
    return selectedRescat ? `.${selectedRescat}` : "";
  }

  if (selectedForecastFolder !== MCDANIEL_SEARCH_FOLDER_NAME) {
    return `.${selectedForecastFolder}.${selectedRescat}`;
  }

  return "";
}

export interface ForecastQualification {
  /** True when the property carries a forecast marker. */
  isQualified: boolean;
  /**
   * The forecast source exactly as the property spells it: the trumping id for the rescat-only
   * form, McDaniel for the bare form, otherwise the source or folder id. Undefined when the
   * property carries no forecast.
   */
  forecastFolder: Maybe<string>;
  /** The reserve category, when the form carries one. */
  rescat: Maybe<string>;
}

/**
 * Reads a qualified property back into the source and rescat that produced it — the inverse of
 * getForecastPropertySuffix, which is why it lives next to it.
 *
 * Rejoining all but the last segment protects a source id that contains a dot, and costs a
 * rescat that contains one: `.fcst.<id>.P.PDP` reads as source `<id>.P`, rescat `PDP`. That is
 * the right way round. Rescats come from user forecast data, source ids are fixed constants, so
 * the exposure sits on the rescat side — and a mis-read rescat degrades one request, while a
 * mis-read source would quietly answer from the wrong forecast.
 */
export function parseForecastQualification(
  property: Maybe<string>
): ForecastQualification {
  if (!isForecastQualifiedProperty(property)) {
    return { isQualified: false, forecastFolder: undefined, rescat: undefined };
  }

  const tail = (property as string).split(`.${FORECAST_KEY}`)[1] ?? "";

  // Test for an empty tail before splitting it. The bare form has nothing after the marker,
  // and "".split(".") is [""] — one segment, which would otherwise read as the rescat-only
  // trumping form and swap EVA Research for Forecast Trumping without a word.
  if (!tail) {
    return {
      isQualified: true,
      forecastFolder: MCDANIEL_SEARCH_FOLDER_NAME,
      rescat: undefined
    };
  }

  const segments = tail.replace(/^\./, "").split(".");

  if (segments.length === 1) {
    return {
      isQualified: true,
      forecastFolder: FORECAST_TRUMPING_FOLDER_ID,
      rescat: segments[0]
    };
  }

  return {
    isQualified: true,
    forecastFolder: segments.slice(0, -1).join("."),
    rescat: segments[segments.length - 1]
  };
}

// The display names the source picker offers, keyed by what the property stores. Kept beside
// the grammar that writes those ids so a tile and the picker cannot disagree.
const FORECAST_FOLDER_DISPLAY_NAMES: Record<string, string> = {
  [FORECAST_TRUMPING_FOLDER_ID]: FORECAST_TRUMPING_FOLDER_NAME,
  [MCDANIEL_SEARCH_FOLDER_NAME]: EVA_RESEARCH_NAME,
  [NOVI_FORECAST_SOURCE]: NOVI_SEARCH_FOLDER_NAME,
  [WORKING_FORECAST_SOURCE]: WORKING_FORECAST_FOLDER_NAME
};

/**
 * The name to show for a stored forecast source. Undefined for anything outside the four
 * built-ins — a user folder id — which leaves the caller to fall back to the id itself.
 */
export function getForecastFolderDisplayName(
  forecastFolder: Maybe<string>
): Maybe<string> {
  return forecastFolder ? FORECAST_FOLDER_DISPLAY_NAMES[forecastFolder] : undefined;
}

/**
 * Separates a property into the bare property and the forecast qualification that follows the
 * marker: the empty string for EVA Research, `.P+PDP` under trumping, `.shaleProfileDB.P+PDP`
 * for a named source.
 *
 * Splitting on the forecast marker rather than inverting each of the three suffix forms is
 * deliberate: an inverse transform written separately from the forward one is free to drift
 * from it, and this runs against properties the forward pass produced.
 *
 * A caller that takes a property apart and puts it back together later must keep the returned
 * qualification verbatim. It cannot be re-derived from the axis alone: the forward pass builds
 * the suffix from the groupBy store's selected folder and rescat, while the axis records only
 * forecastFolder — which for a named source holds the display name, not the source key that
 * went into the property — and no rescat at all.
 */
export function splitForecastQualification(property: string): {
  base: string;
  qualification: string;
} {
  const marker = `.${FORECAST_KEY}`;
  const markerIndex = property.indexOf(marker);
  if (markerIndex < 0) {
    return { base: property, qualification: "" };
  }
  return {
    base: property.slice(0, markerIndex),
    qualification: property.slice(markerIndex + marker.length)
  };
}

/** Strips any forecast qualification back to the bare property. */
function stripForecastQualification(property: string): string {
  return splitForecastQualification(property).base;
}

/**
 * Re-qualifies an already-qualified property for a different forecast source. Used when the
 * source changes without the user re-picking the field, so the committed property keeps
 * describing the data actually being requested.
 */
export function requalifyForecastProperty(
  property: string,
  selectedForecastFolder: Maybe<string>,
  selectedRescat: Maybe<string>
): string {
  const base = stripForecastQualification(property);
  return `${base}.${FORECAST_KEY}${getForecastPropertySuffix(
    selectedForecastFolder,
    selectedRescat
  )}`;
}

/**
 * The forecast qualification stranded in a property by the toggle bug, or "" when the property
 * is healthy.
 *
 * Two shapes, one per toggle state the axis was left in:
 *  - `<base><qualification>.fcst` — forecast on, the marker appended behind the orphan
 *  - `<base><qualification>`      — forecast off, the marker lifted out and nothing put back
 *
 * Neither is decidable from the string alone: `<base>.fcst` is the legitimate EVA Research form,
 * and a bare property is indistinguishable from an orphaned one without knowing what the tail
 * would have been. So the caller supplies the qualifications this axis could be carrying, built
 * from the same forward pass that stamps them, and only an exact tail match counts.
 *
 * Candidates are tried longest first, and each is only accepted if peeling it leaves a base
 * the field list actually knows. Ordering alone is not enough: the candidate list can never be
 * complete — every project forecast folder is offered as `.<folderId>.<rescat>` — so an
 * unlisted source ending in a listed rescat would match the short candidate and weld its
 * source segment into the base, minting a shape with no marker and no recognisable tail that
 * nothing could detect again. Checking the base against the group-by list makes an
 * unrecognised source decline rather than be partially peeled.
 */
function findOrphanedForecastQualification(
  property: string,
  candidateQualifications: string[],
  isKnownProperty: (base: string) => boolean
): string {
  const marker = `.${FORECAST_KEY}`;
  const hasTerminalMarker = property.endsWith(marker);

  // A marker anywhere other than the end means the qualification is already in its right place.
  if (!hasTerminalMarker && property.includes(marker)) {
    return "";
  }

  const tail = hasTerminalMarker ? property.slice(0, -marker.length) : property;

  return (
    [...candidateQualifications]
      .filter(Boolean)
      .sort((a, b) => b.length - a.length)
      .find(
        (candidate) =>
          tail.endsWith(candidate) && isKnownProperty(tail.slice(0, -candidate.length))
      ) ?? ""
  );
}

/**
 * Rebuilds a corrupt property, keeping the source and rescat it was already carrying rather than
 * re-deriving them — so a Novi or synced axis is repaired onto its own source, not onto whatever
 * the panel currently says. Returns null when the property is healthy.
 *
 * The two shapes repair differently because they encode different toggle states. With the marker
 * present the qualification only has to move back in front of it. Without one the axis is off, so
 * the property becomes bare and the qualification is handed back for the toggle to park — which
 * is what lets the next toggle-on restore the right source instead of a bare marker.
 */
export function repairOrphanedForecastProperty(
  property: string,
  candidateQualifications: string[],
  isKnownProperty: (base: string) => boolean
): Nullable<{ property: string; parkedQualification: string }> {
  const orphan = findOrphanedForecastQualification(
    property,
    candidateQualifications,
    isKnownProperty
  );
  if (!orphan) {
    return null;
  }

  const marker = `.${FORECAST_KEY}`;
  const hasTerminalMarker = property.endsWith(marker);
  const base = (hasTerminalMarker ? property.slice(0, -marker.length) : property).slice(
    0,
    -orphan.length
  );

  return hasTerminalMarker
    ? { property: `${base}${marker}${orphan}`, parkedQualification: "" }
    : { property: base, parkedQualification: orphan };
}

/**
 * True when a parked qualification names a forecast source the current selection cannot produce.
 *
 * The refs that detect a panel change are component-lifetime while a parked qualification is
 * persisted with the axis, so a stash outlives the detector across a remount: one parked under
 * Novi comes back in a session where Novi is not selected, and restoring it would put Novi in the
 * request — the same thing getForecastSourceFromSelection refuses to do from a stale panel
 * boolean.
 *
 * Compares the source segment only, never the rescat. The rescat is a free choice the selection
 * does not pin, so treating a non-default one as stale would quietly discard it.
 */
export function isStaleForecastQualification(
  parkedQualification: Maybe<string>,
  selectedForecastFolder: Maybe<string>
): boolean {
  if (!parkedQualification) {
    return false;
  }

  const segments = parkedQualification.split(".").filter(Boolean);

  // Mirrors the three forward forms in getForecastPropertySuffix.
  if (isForecastTrumpingSource(selectedForecastFolder)) {
    // Trumping carries the rescat and no source, so anything longer names one it cannot produce.
    return segments.length > 1;
  }

  if (selectedForecastFolder === MCDANIEL_SEARCH_FOLDER_NAME) {
    // EVA Research carries nothing at all, so any stash is stale.
    return true;
  }

  return segments[0] !== selectedForecastFolder;
}

/** True when the property carries a forecast, i.e. re-qualifying it is meaningful. */
export function isForecastQualifiedProperty(property: Maybe<string>): boolean {
  return !!property && property.includes(`.${FORECAST_KEY}`);
}
